import type { CrossFigure, MoneyOrConflict, RoasBucket, RuleMetric } from "@/services/metaPerformance.service";

/**
 * Money is shown in its own currency and never converted. A figure that
 * mixes currencies is spelled out per currency, and a cross-source figure
 * that could not be computed says why.
 */

export function fmtMoney(amount: number, currency: string, compact = false): string {
  if (!currency) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: compact && Math.abs(amount) >= 1000 ? 0 : 2,
      minimumFractionDigits: compact && Math.abs(amount) >= 1000 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/** Money for a strip: one currency as a figure, several as "Mixed". */
export function fmtMoc(m: MoneyOrConflict | null | undefined, fallbackCurrency = ""): string {
  if (!m) return "—";
  if (m.conflict) return "Mixed";
  return fmtMoney(m.amount, m.currency || fallbackCurrency);
}

/** The per-currency parts of a conflict, for the chip's title or a list. */
export function mocParts(m: MoneyOrConflict): string[] {
  if (!m.conflict) return [fmtMoney(m.amount, m.currency)];
  return m.byCurrency.map((p) => fmtMoney(p.amount, p.currency));
}

export function fmtRoas(v: number | null | undefined): string {
  return v === null || v === undefined || !Number.isFinite(v) ? "—" : `${v.toFixed(2)}x`;
}

export function fmtPct(v: number | null | undefined, digits = 2): string {
  return v === null || v === undefined || !Number.isFinite(v) ? "—" : `${v.toFixed(digits)}%`;
}

export function fmtInt(v: number): string {
  return new Intl.NumberFormat("en-US").format(Math.round(v));
}

export function fmtCross(c: CrossFigure | null | undefined): string {
  if (!c) return "—";
  if (c.value !== null) return fmtRoas(c.value);
  if (c.reason === "CURRENCY_CONFLICT") return "Currency conflict";
  if (c.reason === "NO_SPEND") return "No spend";
  return "—";
}

/** `delta` is a fraction of the previous period, or null when there was none. */
export function fmtDelta(delta: number | null | undefined, days: number): { text: string; direction: "up" | "down" | "flat" } {
  if (delta === null || delta === undefined) return { text: `No prior ${days} days to compare`, direction: "flat" };
  const pct = Math.round(delta * 100);
  if (pct === 0) return { text: `Same as prior ${days} days`, direction: "flat" };
  return { text: `${pct > 0 ? "+" : ""}${pct}% vs prior ${days} days`, direction: pct > 0 ? "up" : "down" };
}

export const BUCKET: Record<RoasBucket, { label: string; hint: string; dot: string; text: string; fill: string }> = {
  SCALE: { label: "Scale", hint: "ROAS at or above the ceiling", dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400", fill: "#10b981" },
  OPTIMIZE: { label: "Optimize", hint: "ROAS between floor and ceiling", dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", fill: "#f59e0b" },
  CUT: { label: "Cut or fix", hint: "ROAS under the floor", dot: "bg-red-500", text: "text-red-700 dark:text-red-400", fill: "#ef4444" },
  UNRATED: { label: "Not enough spend", hint: "Below the minimum spend to judge", dot: "bg-muted-foreground/60", text: "text-muted-foreground", fill: "hsl(var(--muted-foreground) / 0.45)" },
};

export const METRIC_LABEL: Record<RuleMetric, string> = {
  ROAS_FLOOR: "ROAS under floor",
  ROAS_CEILING: "ROAS over ceiling",
  CTR_FLOOR: "CTR under floor",
  CTR_CEILING: "CTR over ceiling",
};

export function fmtMetricValue(metric: RuleMetric, v: number): string {
  return metric.startsWith("ROAS") ? fmtRoas(v) : fmtPct(v);
}

/** "2026-10-12" → "Oct 12". */
export function shortDay(key: string): string {
  const d = new Date(`${key}T00:00:00`);
  return Number.isNaN(d.getTime()) ? key : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function objectiveLabel(objective: string | null): string {
  if (!objective) return "—";
  return objective.replace(/^OUTCOME_/, "").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}
